import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Logo } from "../../components/Logo";
import { LanguageToggle } from "../../components/LanguageToggle";
import { toast } from "sonner";
import { MapPin, Clock, IdCard, FileText, LogOut, CheckCircle2, Fingerprint } from "lucide-react";

export default function StaffPortal() {
  const { t, isAr, fmtDate } = useI18n();
  const { user, isStaff, logout, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/me/staff").then((r) => { setData(r.data); if (r.data.profile?.branch_ids?.length) setBranchId(r.data.profile.branch_ids[0]); }).catch(() => {});
  useEffect(() => { if (loading) return; if (!user) { nav("/login"); return; } if (!isStaff) nav("/account"); }, [user, isStaff, nav, loading]);
  useEffect(() => { if (isStaff) { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); } }, [isStaff]);
  if (!user || !isStaff) return null;

  const myBranches = branches.filter((b) => !data?.profile?.branch_ids?.length || data.profile.branch_ids.includes(b.id));

  const checkin = () => {
    if (!branchId) { toast.error(isAr ? "اختر الفرع" : "Select branch"); return; }
    if (!navigator.geolocation) { toast.error(isAr ? "الموقع غير مدعوم" : "Geolocation unsupported"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data: r } = await api.post("/admin/attendance/checkin", { branch_id: branchId, lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success((r.kind === "in" ? (isAr ? "تم تسجيل الحضور" : "Checked in") : (isAr ? "تم تسجيل الانصراف" : "Checked out")) + ` · ${r.distance_m}m`);
        load();
      } catch (e) { toast.error(apiError(e)); }
      finally { setBusy(false); }
    }, () => { toast.error(isAr ? "تعذّر تحديد موقعك" : "Could not get location"); setBusy(false); }, { enableHighAccuracy: true });
  };

  const p = data?.profile || {};
  const hr = [["iqama", isAr ? "رقم الإقامة" : "Iqama", IdCard], ["iqama_expiry", isAr ? "انتهاء الإقامة" : "Iqama expiry", Clock], ["license", isAr ? "الرخصة" : "License", FileText], ["license_expiry", isAr ? "انتهاء الرخصة" : "License expiry", Clock], ["contract_start", isAr ? "بداية العقد" : "Contract start", FileText]];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 sm:px-8">
        <Logo height={34} />
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <button onClick={() => { logout(); nav("/"); }} data-testid="portal-logout" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm text-[hsl(var(--brand-red))]"><LogOut className="h-4 w-4" /> {t("nav.logout")}</button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8">
        <h1 className="text-2xl font-bold text-slate-900">{isAr ? "بوابة الموظف" : "Staff Portal"}</h1>
        <p className="text-sm text-slate-500">{p.name} · <b className="text-primary">{p.role}</b></p>

        {/* Check-in card */}
        <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-lg font-bold text-slate-900"><Fingerprint className="h-5 w-5 text-primary" /> {isAr ? "الحضور والانصراف" : "Attendance"}</div>
          <p className="mt-1 text-sm text-slate-500">{isAr ? "يجب أن تكون ضمن 100 متر من الفرع" : "You must be within 100m of the branch"}</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="portal-branch" className="flex-1 rounded-xl border border-border px-4 py-3 text-sm">
              <option value="">{isAr ? "اختر الفرع" : "Select branch"}</option>
              {myBranches.map((b) => <option key={b.id} value={b.id}>{isAr ? b.name_ar : b.name_en}</option>)}
            </select>
            <button onClick={checkin} disabled={busy} data-testid="portal-checkin-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-8 py-3 font-semibold text-white disabled:opacity-50">
              <MapPin className="h-5 w-5" /> {busy ? t("common.loading") : (isAr ? "تسجيل الحضور/الانصراف" : "Check in / out")}
            </button>
          </div>
        </div>

        {/* HR data */}
        <div className="mt-6 rounded-3xl border border-border bg-white p-6">
          <h2 className="font-bold text-slate-900">{isAr ? "بياناتي" : "My details"}</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {hr.map(([k, label, Icon]) => (
              <div key={k} className="flex items-center gap-3 rounded-xl border border-border p-3">
                <Icon className="h-5 w-5 text-slate-400" />
                <div><div className="text-xs text-slate-400">{label}</div><div className="text-sm font-medium text-slate-800" dir="ltr">{p[k] || "—"}</div></div>
              </div>
            ))}
          </div>
        </div>

        {/* Attendance history */}
        <div className="mt-6 rounded-3xl border border-border bg-white p-6">
          <h2 className="font-bold text-slate-900">{isAr ? "سجل الحضور" : "Attendance history"}</h2>
          <div className="mt-4 space-y-2">
            {(data?.attendance || []).length === 0 && <p className="py-6 text-center text-sm text-slate-400">{t("common.none")}</p>}
            {(data?.attendance || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">
                <span className="inline-flex items-center gap-2"><CheckCircle2 className={`h-4 w-4 ${a.kind === "in" ? "text-green-600" : "text-slate-400"}`} /> {a.kind === "in" ? (isAr ? "حضور" : "In") : (isAr ? "انصراف" : "Out")}</span>
                <span className="text-slate-500">{fmtDate(a.at).greg} · {fmtDate(a.at).time}</span>
                <span className="text-xs text-slate-400" dir="ltr">{a.distance_m}m</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
